import jsPDF from 'jspdf'
import 'jspdf-autotable'
import type { Invoice } from '../types/invoice'

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF
  }
}

export class InvoicePDFGenerator {
  private doc: jsPDF
  private pageWidth: number
  private pageHeight: number
  private margin: number

  constructor() {
    this.doc = new jsPDF()
    this.pageWidth = this.doc.internal.pageSize.getWidth()
    this.pageHeight = this.doc.internal.pageSize.getHeight()
    this.margin = 20
  }

  generateInvoicePDF(invoice: Invoice, userProfile?: any): Blob {
    try {
      this.doc = new jsPDF()
      
      // Header
      this.addHeader(invoice, userProfile)
      
      // Invoice details
      this.addInvoiceDetails(invoice)
      
      // Client information
      this.addClientInfo(invoice)
      
      // Line items table
      this.addLineItems(invoice)
      
      // Totals
      this.addTotals(invoice)
      
      // Footer
      this.addFooter(invoice)
      
      return this.doc.output('blob')
    } catch (error) {
      console.error('Error in PDF generation:', error)
      throw new Error(`PDF generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  private addHeader(invoice: Invoice, userProfile?: any) {
    // Company/User name
    this.doc.setFontSize(24)
    this.doc.setFont('helvetica', 'bold')
    this.doc.setTextColor(44, 62, 80)
    
    const companyName = userProfile?.businessName || `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim() || 'ShootSuite User'
    this.doc.text(companyName, this.margin, 30)
    
    // Contact info
    this.doc.setFontSize(10)
    this.doc.setFont('helvetica', 'normal')
    this.doc.setTextColor(127, 140, 141)
    
    let yPos = 40
    if (userProfile?.email) {
      this.doc.text(userProfile.email, this.margin, yPos)
      yPos += 6
    }
    if (userProfile?.phone) {
      this.doc.text(userProfile.phone, this.margin, yPos)
      yPos += 6
    }
    if (userProfile?.address) {
      this.doc.text(userProfile.address, this.margin, yPos)
      yPos += 6
    }
    
    // Invoice title
    this.doc.setFontSize(32)
    this.doc.setFont('helvetica', 'bold')
    this.doc.setTextColor(52, 152, 219)
    this.doc.text('INVOICE', this.pageWidth - this.margin - 60, 30)
    
    // Invoice number
    this.doc.setFontSize(12)
    this.doc.setFont('helvetica', 'normal')
    this.doc.setTextColor(44, 62, 80)
    this.doc.text(`#${invoice.invoiceNumber}`, this.pageWidth - this.margin - 60, 45)
  }

  private addInvoiceDetails(invoice: Invoice) {
    const startY = 80
    
    this.doc.setFontSize(10)
    this.doc.setFont('helvetica', 'bold')
    this.doc.setTextColor(44, 62, 80)
    
    // Invoice details box
    this.doc.setDrawColor(189, 195, 199)
    this.doc.setLineWidth(0.5)
    this.doc.rect(this.pageWidth - this.margin - 80, startY, 80, 40)
    
    // Details
    let yPos = startY + 10
    this.doc.text('Issue Date:', this.pageWidth - this.margin - 75, yPos)
    this.doc.setFont('helvetica', 'normal')
    this.doc.text(this.formatDate(invoice.issueDate), this.pageWidth - this.margin - 35, yPos)
    
    yPos += 8
    this.doc.setFont('helvetica', 'bold')
    this.doc.text('Due Date:', this.pageWidth - this.margin - 75, yPos)
    this.doc.setFont('helvetica', 'normal')
    this.doc.text(this.formatDate(invoice.dueDate), this.pageWidth - this.margin - 35, yPos)
    
    yPos += 8
    this.doc.setFont('helvetica', 'bold')
    this.doc.text('Status:', this.pageWidth - this.margin - 75, yPos)
    this.doc.setFont('helvetica', 'normal')
    
    // Status with color
    const statusColor = this.getStatusColor(invoice.status)
    this.doc.setTextColor(statusColor.r, statusColor.g, statusColor.b)
    this.doc.text(invoice.status.toUpperCase(), this.pageWidth - this.margin - 35, yPos)
    this.doc.setTextColor(44, 62, 80) // Reset color
  }

  private addClientInfo(invoice: Invoice) {
    const startY = 130
    
    this.doc.setFontSize(14)
    this.doc.setFont('helvetica', 'bold')
    this.doc.setTextColor(44, 62, 80)
    this.doc.text('Bill To:', this.margin, startY)
    
    if (invoice.client) {
      this.doc.setFontSize(12)
      this.doc.setFont('helvetica', 'bold')
      this.doc.text(invoice.client.name, this.margin, startY + 15)
      
      this.doc.setFont('helvetica', 'normal')
      this.doc.setTextColor(127, 140, 141)
      
      let yPos = startY + 25
      if (invoice.client.company) {
        this.doc.text(invoice.client.company, this.margin, yPos)
        yPos += 10
      }
      this.doc.text(invoice.client.email, this.margin, yPos)
    }
  }

  private addLineItems(invoice: Invoice) {
    const startY = 180
    
    if (!invoice.items || invoice.items.length === 0) return
    
    try {
      // Check if autoTable is available
      if (typeof this.doc.autoTable !== 'function') {
        console.warn('autoTable not available, using manual table creation')
        this.addLineItemsManual(invoice, startY)
        return
      }
      
      // Prepare table data
      const tableData = invoice.items.map(item => [
        item.description,
        item.quantity.toString(),
        this.formatCurrency(item.unitPrice),
        this.formatCurrency(item.totalPrice)
      ])
      
      // Add table
      this.doc.autoTable({
        startY: startY,
        head: [['Description', 'Qty', 'Rate', 'Amount']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [52, 152, 219],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 10
        },
        bodyStyles: {
          fontSize: 9,
          textColor: [44, 62, 80]
        },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: 20, halign: 'center' },
          2: { cellWidth: 30, halign: 'right' },
          3: { cellWidth: 30, halign: 'right' }
        },
        margin: { left: this.margin, right: this.margin },
        styles: {
          lineColor: [189, 195, 199],
          lineWidth: 0.1
        }
      })
    } catch (error) {
      console.error('Error with autoTable, falling back to manual table:', error)
      this.addLineItemsManual(invoice, startY)
    }
  }

  private addLineItemsManual(invoice: Invoice, startY: number) {
    this.doc.setFontSize(14)
    this.doc.setFont('helvetica', 'bold')
    this.doc.setTextColor(44, 62, 80)
    this.doc.text('Items:', this.margin, startY)
    
    let yPos = startY + 20
    
    // Header
    this.doc.setFontSize(10)
    this.doc.setFont('helvetica', 'bold')
    this.doc.text('Description', this.margin, yPos)
    this.doc.text('Qty', this.margin + 100, yPos)
    this.doc.text('Rate', this.margin + 130, yPos)
    this.doc.text('Amount', this.margin + 160, yPos)
    
    yPos += 10
    
    // Items
    this.doc.setFont('helvetica', 'normal')
    invoice.items?.forEach(item => {
      this.doc.text(item.description, this.margin, yPos)
      this.doc.text(item.quantity.toString(), this.margin + 100, yPos)
      this.doc.text(this.formatCurrency(item.unitPrice), this.margin + 130, yPos)
      this.doc.text(this.formatCurrency(item.totalPrice), this.margin + 160, yPos)
      yPos += 8
    })
  }

  private addTotals(invoice: Invoice) {
    // Calculate position based on items count or use default
    const itemsCount = invoice.items?.length || 0
    const estimatedTableHeight = 180 + 20 + (itemsCount * 8) + 40 // startY + header + items + padding
    const finalY = (this.doc as any).lastAutoTable?.finalY || estimatedTableHeight
    const startY = finalY + 20
    const rightX = this.pageWidth - this.margin - 60
    
    this.doc.setFontSize(10)
    this.doc.setFont('helvetica', 'normal')
    this.doc.setTextColor(44, 62, 80)
    
    let yPos = startY
    
    // Subtotal
    this.doc.text('Subtotal:', rightX, yPos)
    this.doc.text(this.formatCurrency(invoice.subtotal), rightX + 40, yPos)
    yPos += 8
    
    // Discount
    if (invoice.discountAmount > 0) {
      this.doc.text('Discount:', rightX, yPos)
      this.doc.text(`-${this.formatCurrency(invoice.discountAmount)}`, rightX + 40, yPos)
      yPos += 8
    }
    
    // Tax
    if (invoice.taxRate > 0) {
      this.doc.text(`Tax (${invoice.taxRate}%):`, rightX, yPos)
      this.doc.text(this.formatCurrency(invoice.taxAmount), rightX + 40, yPos)
      yPos += 8
    }
    
    // Total line
    this.doc.setLineWidth(0.5)
    this.doc.setDrawColor(189, 195, 199)
    this.doc.line(rightX, yPos, rightX + 50, yPos)
    yPos += 10
    
    // Total amount
    this.doc.setFontSize(14)
    this.doc.setFont('helvetica', 'bold')
    this.doc.setTextColor(52, 152, 219)
    this.doc.text('Total:', rightX, yPos)
    this.doc.text(this.formatCurrency(invoice.totalAmount), rightX + 40, yPos)
  }

  private addFooter(invoice: Invoice) {
    const footerY = this.pageHeight - 60
    
    // Terms and conditions
    if (invoice.terms) {
      this.doc.setFontSize(10)
      this.doc.setFont('helvetica', 'bold')
      this.doc.setTextColor(44, 62, 80)
      this.doc.text('Terms & Conditions:', this.margin, footerY)
      
      this.doc.setFont('helvetica', 'normal')
      this.doc.setTextColor(127, 140, 141)
      const splitTerms = this.doc.splitTextToSize(invoice.terms, this.pageWidth - 2 * this.margin)
      this.doc.text(splitTerms, this.margin, footerY + 10)
    }
    
    // Notes
    if (invoice.notes) {
      const notesY = invoice.terms ? footerY + 30 : footerY
      this.doc.setFontSize(10)
      this.doc.setFont('helvetica', 'bold')
      this.doc.setTextColor(44, 62, 80)
      this.doc.text('Notes:', this.margin, notesY)
      
      this.doc.setFont('helvetica', 'normal')
      this.doc.setTextColor(127, 140, 141)
      const splitNotes = this.doc.splitTextToSize(invoice.notes, this.pageWidth - 2 * this.margin)
      this.doc.text(splitNotes, this.margin, notesY + 10)
    }
    
    // Footer text
    if (invoice.footerText) {
      this.doc.setFontSize(8)
      this.doc.setFont('helvetica', 'normal')
      this.doc.setTextColor(149, 165, 166)
      this.doc.text(invoice.footerText, this.pageWidth / 2, this.pageHeight - 20, { align: 'center' })
    }
  }

  private formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  private getStatusColor(status: string): { r: number; g: number; b: number } {
    switch (status) {
      case 'paid':
        return { r: 39, g: 174, b: 96 } // Green
      case 'sent':
        return { r: 52, g: 152, b: 219 } // Blue
      case 'overdue':
        return { r: 231, g: 76, b: 60 } // Red
      case 'draft':
        return { r: 149, g: 165, b: 166 } // Gray
      default:
        return { r: 44, g: 62, b: 80 } // Dark gray
    }
  }
}

export const generateInvoicePDF = (invoice: Invoice, userProfile?: any): Blob => {
  const generator = new InvoicePDFGenerator()
  return generator.generateInvoicePDF(invoice, userProfile)
} 